import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Row, Col, CardBody, Table } from "reactstrap";
import NeoButton from "../../../components/Common/NeoButton";
import NeoCard from "../../../components/Common/NeoCard";
import NeoBadge from "../../../components/Common/NeoBadge";
import Breadcrumb from "../../../components/Common/Breadcrumb";
import { listPortfolio } from "../../../services/platform_csm_service";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../../../services/firebase";
import { listAuditLogs } from "../../../services/platform_admin_service";
import { ROLE_LABELS } from "./constants";

const PlatformUserDetail = () => {
    const { id } = useParams();
    const navigate = useNavigate();

    const [user, setUser] = useState(null);
    const [loadingUser, setLoadingUser] = useState(true);
    const [activeTab, setActiveTab] = useState("info");
    const [portfolioItems, setPortfolioItems] = useState([]);
    const [loadingPortfolio, setLoadingPortfolio] = useState(false);
    const [userLogs, setUserLogs] = useState([]);
    const [loadingLogs, setLoadingLogs] = useState(false);

    // Fetch user details from Firestore
    useEffect(() => {
        const fetchUser = async () => {
            try {
                setLoadingUser(true);
                const userDocRef = doc(db, "users", id);
                const userSnap = await getDoc(userDocRef);
                if (userSnap.exists()) {
                    const data = userSnap.data();
                    const addr = data.address || {};
                    setUser({
                        id: userSnap.id,
                        name: data.fullName || data.name || "Sem Nome",
                        email: data.email || "—",
                        role: data.platformRole || data.role || "employee",
                        position: data.position || "—",
                        phone: data.phone || "—",
                        status: data.status || "active",
                        lastLogin: data.lastLogin ? new Date(data.lastLogin).toLocaleString("pt-BR") : "—",
                        photoUrl: data.photoUrl || "https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?w=200&h=200&fit=crop&q=80",
                        zipCode: addr.zipCode || data.zipCode || "—",
                        street: addr.street || data.street || "—",
                        number: addr.number || data.number || "—",
                        neighborhood: addr.neighborhood || data.neighborhood || "—",
                        city: addr.city || data.city || "—",
                        state: addr.state || data.state || "—",
                        complement: addr.complement || data.complement || "—",
                        createdAt: data.createdAt 
                            ? (typeof data.createdAt.toDate === "function" 
                                ? data.createdAt.toDate().toLocaleDateString("pt-BR") 
                                : new Date(data.createdAt).toLocaleDateString("pt-BR"))
                            : "—",
                    });
                } else {
                    setUser(null);
                }
            } catch (err) {
                console.error("Erro ao carregar usuário:", err);
                setUser(null);
            } finally {
                setLoadingUser(false);
            }
        };
        if (id) {
            fetchUser();
        }
    }, [id]);

    // Fetch portfolio items
    useEffect(() => {
        if (activeTab === "portfolio" && id) {
            const loadPortfolio = async () => {
                setLoadingPortfolio(true);
                try {
                    const result = await listPortfolio({ userId: id, view: "csm" });
                    setPortfolioItems(result.items || []);
                } catch (e) {
                    console.error("Erro ao carregar carteira:", e);
                } finally {
                    setLoadingPortfolio(false);
                }
            };
            loadPortfolio();
        }
    }, [activeTab, id]);

    // Fetch audit logs
    useEffect(() => {
        if (activeTab === "logs" && id) {
            const loadLogs = async () => {
                setLoadingLogs(true);
                try {
                    const result = await listAuditLogs({
                        page: 1,
                        pageSize: 50,
                        filters: { actorUid: id }
                    });
                    const items = (result.items || []).map((log) => ({
                        id: log.id,
                        action: log.action || "—",
                        target: log.targetTenantName || log.targetId || "—",
                        desc: log.description || "—",
                        date: log.createdAt
                            ? new Date(log.createdAt).toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" })
                            : "—",
                    }));
                    setUserLogs(items);
                } catch (e) {
                    console.error("Erro ao carregar logs do usuário:", e);
                } finally {
                    setLoadingLogs(false);
                }
            };
            loadLogs();
        }
    }, [activeTab, id]);

    if (loadingUser) {
        return (
            <div className="pb-5 d-flex align-items-center justify-content-center" style={{ minHeight: "300px", fontFamily: "'Outfit', 'Inter', sans-serif" }}>
                <div className="text-center py-5">
                    <div className="spinner-border text-primary" role="status" />
                    <p className="text-muted mt-3">Carregando perfil do colaborador...</p>
                </div>
            </div>
        );
    }

    if (!user) {
        return (
            <div className="pb-5" style={{ fontFamily: "'Outfit', 'Inter', sans-serif" }}>
                <div className="d-flex align-items-center justify-content-between mb-4">
                    <Breadcrumb title="Perfil não encontrado" />
                    <NeoButton variant="soft-secondary" size="sm" onClick={() => navigate("/platform/users")}>
                        <i className="mdi mdi-arrow-left me-1" /> Voltar para Equipe
                    </NeoButton>
                </div>
                <div className="alert alert-danger" role="alert">
                    Colaborador não encontrado ou excluído.
                </div>
            </div>
        );
    }

    const getRoleColor = (role) => {
        const r = (role || "").toLowerCase();
        if (r === "owner") return "danger";
        if (r === "admin") return "accent";
        if (r === "financeiro" || r === "finance") return "success";
        return "warning";
    };

    return (
        <div className="pb-5" style={{ fontFamily: "'Outfit', 'Inter', sans-serif" }}>
            {/* Header / Breadcrumb e Botão Voltar */}
            <div className="d-flex align-items-center justify-content-between mb-4">
                <Breadcrumb title={`Perfil: ${user.name}`} />
                <NeoButton variant="soft-secondary" size="sm" onClick={() => navigate("/platform/users")}>
                    <i className="mdi mdi-arrow-left me-1" /> Voltar para Equipe
                </NeoButton>
            </div>

            <Row className="g-4">
                {/* 1. CARD DE DESTAQUE LATERAL (FOTO E INFORMAÇÕES DE ACESSO) */}
                <Col lg={4}>
                    <NeoCard thinBorder={true} className="text-center p-4 h-100">
                        <div className="position-relative d-inline-block mx-auto mb-4">
                            <img
                                src={user.photoUrl}
                                alt={user.name}
                                className="rounded-circle shadow"
                                style={{
                                    width: "150px",
                                    height: "150px",
                                    objectFit: "cover",
                                    border: "3px solid #482712"
                                }}
                            />
                        </div>

                        <h4 className="fw-bold text-dark mb-1">{user.name}</h4>
                        <p className="text-muted small fw-semibold mb-3">{user.position}</p>

                        <div className="d-flex justify-content-center gap-2 mb-4">
                            <NeoBadge color={getRoleColor(user.role)}>
                                {ROLE_LABELS[user.role] || user.role}
                            </NeoBadge>
                            <NeoBadge color={user.status === "active" ? "success" : "danger"}>
                                {user.status === "active" ? "Acesso Ativo" : "Bloqueado"}
                            </NeoBadge>
                        </div>

                        <hr style={{ borderColor: "rgba(72, 39, 18, 0.08)" }} />

                        <div className="text-start mt-4">
                            <div className="mb-3">
                                <small className="text-muted d-block font-size-10 text-uppercase fw-bold">E-mail de Trabalho</small>
                                <span className="fw-bold text-dark font-size-13">{user.email}</span>
                            </div>
                            <div className="mb-3">
                                <small className="text-muted d-block font-size-10 text-uppercase fw-bold">Celular / WhatsApp</small>
                                <span className="fw-bold text-dark font-size-13">{user.phone}</span>
                            </div>
                            <div className="mb-3">
                                <small className="text-muted d-block font-size-10 text-uppercase fw-bold">Último Login Realizado</small>
                                <span className="fw-bold text-dark font-size-13">{user.lastLogin}</span>
                            </div>
                            <div>
                                <small className="text-muted d-block font-size-10 text-uppercase fw-bold">Data de Contratação</small>
                                <span className="fw-bold text-dark font-size-13">{user.createdAt}</span>
                            </div>
                        </div>
                    </NeoCard>
                </Col>

                {/* 2. CONTEÚDO PRINCIPAL (ABAS DE INFORMAÇÕES RESIDENCIAIS E LOGS DE GESTÃO) */}
                <Col lg={8}>
                    <NeoCard thinBorder={true} className="h-100 p-0">
                        {/* Navegação por Abas */}
                        <div className="d-flex border-bottom px-4 pt-3 gap-4" style={{ background: "#fcf8f4" }}>
                            <button
                                className="pb-3 fw-bold border-bottom-2 position-relative transition-all bg-transparent border-0"
                                onClick={() => setActiveTab("info")}
                                style={{
                                    fontSize: "13px",
                                    color: activeTab === "info" ? "#e2661c" : "#784b22",
                                    borderBottom: activeTab === "info" ? "3px solid #e2661c" : "3px solid transparent",
                                    fontWeight: activeTab === "info" ? "800" : "600",
                                }}
                            >
                                <i className="mdi mdi-map-marker-outline me-1" /> Endereço Residencial
                            </button>
                            <button
                                className="pb-3 fw-bold border-bottom-2 position-relative transition-all bg-transparent border-0"
                                onClick={() => setActiveTab("logs")}
                                style={{
                                    fontSize: "13px",
                                    color: activeTab === "logs" ? "#e2661c" : "#784b22",
                                    borderBottom: activeTab === "logs" ? "3px solid #e2661c" : "3px solid transparent",
                                    fontWeight: activeTab === "logs" ? "800" : "600",
                                }}
                            >
                                <i className="mdi mdi-chart-bubble me-1" /> Registro de Ações (Auditoria)
                            </button>
                            <button
                                className="pb-3 fw-bold border-bottom-2 position-relative transition-all bg-transparent border-0"
                                onClick={() => setActiveTab("portfolio")}
                                style={{
                                    fontSize: "13px",
                                    color: activeTab === "portfolio" ? "#e2661c" : "#784b22",
                                    borderBottom: activeTab === "portfolio" ? "3px solid #e2661c" : "3px solid transparent",
                                    fontWeight: activeTab === "portfolio" ? "800" : "600",
                                }}
                            >
                                <i className="mdi mdi-briefcase-outline me-1" /> Carteira de Clientes
                            </button>
                        </div>

                        <CardBody className="p-4">
                            {/* ABA 1: ENDEREÇO RESIDENCIAL */}
                            {activeTab === "info" && (
                                <div className="animate__animated animate__fadeIn">
                                    <h6 className="text-uppercase font-size-11 fw-bold text-muted mb-4" style={{ letterSpacing: "0.5px" }}>
                                        Endereço Cadastrado
                                    </h6>

                                    <Row className="g-3 mb-4">
                                        <Col md={3}>
                                            <div className="p-3 bg-light rounded-3">
                                                <small className="text-muted d-block font-size-10 text-uppercase fw-bold">CEP</small>
                                                <span className="fw-bold text-dark font-size-13">{user.zipCode || "—"}</span>
                                            </div>
                                        </Col>
                                        <Col md={6}>
                                            <div className="p-3 bg-light rounded-3">
                                                <small className="text-muted d-block font-size-10 text-uppercase fw-bold">Logradouro / Rua</small>
                                                <span className="fw-bold text-dark font-size-13">{user.street || "—"}</span>
                                            </div>
                                        </Col>
                                        <Col md={3}>
                                            <div className="p-3 bg-light rounded-3">
                                                <small className="text-muted d-block font-size-10 text-uppercase fw-bold">Número</small>
                                                <span className="fw-bold text-dark font-size-13">{user.number || "—"}</span>
                                            </div>
                                        </Col>
                                        <Col md={4}>
                                            <div className="p-3 bg-light rounded-3">
                                                <small className="text-muted d-block font-size-10 text-uppercase fw-bold">Bairro</small>
                                                <span className="fw-bold text-dark font-size-13">{user.neighborhood || "—"}</span>
                                            </div>
                                        </Col>
                                        <Col md={4}>
                                            <div className="p-3 bg-light rounded-3">
                                                <small className="text-muted d-block font-size-10 text-uppercase fw-bold">Cidade</small>
                                                <span className="fw-bold text-dark font-size-13">{user.city || "—"}</span>
                                            </div>
                                        </Col>
                                        <Col md={4}>
                                            <div className="p-3 bg-light rounded-3">
                                                <small className="text-muted d-block font-size-10 text-uppercase fw-bold">Estado</small>
                                                <span className="fw-bold text-dark font-size-13">{user.state || "—"}</span>
                                            </div>
                                        </Col>
                                        <Col md={12}>
                                            <div className="p-3 bg-light rounded-3">
                                                <small className="text-muted d-block font-size-10 text-uppercase fw-bold">Complemento</small>
                                                <span className="fw-bold text-dark font-size-13">{user.complement || "—"}</span>
                                            </div>
                                        </Col>
                                    </Row>
                                </div>
                            )}

                            {/* ABA 2: REGISTRO DE AÇÕES (AUDITORIA) */}
                            {activeTab === "logs" && (
                                <div className="animate__animated animate__fadeIn">
                                    <h6 className="text-uppercase font-size-11 fw-bold text-muted mb-3" style={{ letterSpacing: "0.5px" }}>
                                        Logs de Auditoria de {user.name}
                                    </h6>
                                    <p className="text-muted font-size-12 mb-4">
                                        Histórico recente de atividades e alterações realizadas por este usuário no painel gerencial do SaaS.
                                    </p>

                                    {loadingLogs ? (
                                        <div className="text-center py-5 text-muted">
                                            <i className="bx bx-loader bx-spin font-size-24 align-middle me-2" />
                                            Carregando histórico de ações...
                                        </div>
                                    ) : userLogs && userLogs.length > 0 ? (
                                        <div className="table-responsive">
                                            <Table className="table-striped table-bordered align-middle">
                                                <thead className="table-light">
                                                    <tr>
                                                        <th style={{ fontSize: "11px", fontWeight: "bold" }}>Ação / Evento</th>
                                                        <th style={{ fontSize: "11px", fontWeight: "bold" }}>Alvo</th>
                                                        <th style={{ fontSize: "11px", fontWeight: "bold" }}>Detalhamento</th>
                                                        <th style={{ fontSize: "11px", fontWeight: "bold" }} className="text-center">Data/Hora</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {userLogs.map((log) => (
                                                        <tr key={log.id}>
                                                            <td>
                                                                <span className="fw-bold text-dark font-size-12">{log.action}</span>
                                                            </td>
                                                            <td>
                                                                <span className="text-muted font-size-12">{log.target}</span>
                                                            </td>
                                                            <td>
                                                                <span className="font-size-12 text-secondary">{log.desc}</span>
                                                            </td>
                                                            <td className="text-center">
                                                                <span className="text-muted font-size-11 fw-semibold">{log.date}</span>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </Table>
                                        </div>
                                    ) : (
                                        <div className="text-center py-5 text-muted">
                                            <i className="mdi mdi-text-box-remove-outline font-size-24 opacity-50 mb-2 d-block" />
                                            <span className="fw-semibold">Nenhuma ação registrada para este colaborador.</span>
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* ABA 3: CARTEIRA DE CLIENTES (PORTFOLIO) */}
                            {activeTab === "portfolio" && (
                                <div className="animate__animated animate__fadeIn">
                                    <h6 className="text-uppercase font-size-11 fw-bold text-muted mb-3" style={{ letterSpacing: "0.5px" }}>
                                        Carteira de Clientes de {user.name}
                                    </h6>
                                    <p className="text-muted font-size-12 mb-4">
                                        Lista de academias sob responsabilidade direta de pós-venda (CSM) deste colaborador.
                                    </p>

                                    {loadingPortfolio ? (
                                        <div className="text-center py-5 text-muted">
                                            <i className="bx bx-loader bx-spin font-size-24 align-middle me-2" />
                                            Carregando carteira de clientes...
                                        </div>
                                    ) : portfolioItems.length > 0 ? (
                                        <div className="table-responsive">
                                            <Table className="table-striped table-bordered align-middle">
                                                <thead className="table-light">
                                                    <tr>
                                                        <th style={{ fontSize: "11px", fontWeight: "bold" }}>Academia</th>
                                                        <th style={{ fontSize: "11px", fontWeight: "bold" }}>Status</th>
                                                        <th style={{ fontSize: "11px", fontWeight: "bold" }}>Onboarding</th>
                                                        <th style={{ fontSize: "11px", fontWeight: "bold" }} className="text-center">Saúde</th>
                                                        <th style={{ fontSize: "11px", fontWeight: "bold" }} className="text-center">Ações</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {portfolioItems.map((item) => (
                                                        <tr key={item.id}>
                                                            <td>
                                                                <span className="fw-bold text-dark font-size-12 d-block">{item.name}</span>
                                                                <span className="text-muted font-size-10">{item.ownerName || "—"}</span>
                                                            </td>
                                                            <td>
                                                                <NeoBadge color={item.billingStatus === "active" ? "success" : item.billingStatus === "trial_started" ? "accent" : "danger"}>
                                                                    {item.billingStatus === "active" ? "Ativo" : item.billingStatus === "trial_started" ? "Trial" : item.billingStatus || "—"}
                                                                </NeoBadge>
                                                            </td>
                                                            <td>
                                                                <div className="d-flex align-items-center gap-2" style={{ maxWidth: 120 }}>
                                                                    <div className="progress flex-grow-1" style={{ height: "4px", backgroundColor: "rgba(72, 39, 18, 0.08)" }}>
                                                                        <div className="progress-bar" style={{ width: `${item.onboardingProgress?.pct ?? 0}%`, backgroundColor: (item.onboardingProgress?.pct ?? 0) === 100 ? "#2e7d32" : "var(--color-brand-accent)" }} />
                                                                    </div>
                                                                    <span className="font-size-10 fw-bold">{item.onboardingProgress?.pct ?? 0}%</span>
                                                                </div>
                                                            </td>
                                                            <td className="text-center">
                                                                <NeoBadge color={item.health?.level === "green" ? "success" : item.health?.level === "yellow" ? "warning" : "danger"}>
                                                                    {item.health?.score ?? "—"}
                                                                </NeoBadge>
                                                            </td>
                                                            <td className="text-center">
                                                                <NeoButton
                                                                    variant="soft-secondary"
                                                                    size="xs"
                                                                    onClick={() => navigate("/platform/portfolio")}
                                                                >
                                                                    Ver Carteira
                                                                </NeoButton>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </Table>
                                        </div>
                                    ) : (
                                        <div className="text-center py-5 text-muted">
                                            <i className="ph-light ph-briefcase font-size-36 opacity-50 mb-2 d-block" />
                                            <span className="fw-semibold">Nenhum cliente na carteira deste CS.</span>
                                        </div>
                                    )}
                                </div>
                            )}
                        </CardBody>
                    </NeoCard>
                </Col>
            </Row>
        </div>
    );
};

export default PlatformUserDetail;
